import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import type { Problem } from '../api/types';

export interface AsyncState<T> {
  data: T | null;
  /**
   * 失敗はすべてproblem+json形と同じ形で持つ。
   * 通信断のようにサーバー応答が無い失敗も status=0 / code=transport_error として同じ経路へ寄せ、
   * 「片方だけ描画し忘れて画面が沈黙する」状態を作らない。
   */
  problem: Problem | null;
  loading: boolean;
  reload(): void;
}

/**
 * 画面がsnapshotを取り直すための最小限のhook。
 * SSEを受けたら reload() を呼ぶ、という使い方を想定している。
 */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [state, setState] = useState<{
    data: T | null;
    problem: Problem | null;
    loading: boolean;
  }>({ data: null, problem: null, loading: true });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    setState((current) => ({ ...current, loading: true }));
    load()
      .then((data) => {
        if (!active) return;
        setState({ data, problem: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState({ data: null, problem: toProblem(error), loading: false });
      });
    return () => {
      active = false;
    };
    // loadは呼び出し側が毎回作り直すため、依存はdepsだけを使う。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);
  return { ...state, reload };
}

/** サーバー応答が無い失敗も、画面が同じ部品で出せるようproblemの形に揃える。 */
export function toProblem(error: unknown): Problem {
  if (error instanceof ApiError) return error.problem;
  return {
    type: 'about:blank',
    title: '通信に失敗しました',
    status: 0,
    code: 'transport_error',
    detail: error instanceof Error ? error.message : String(error),
    traceId: '',
  };
}
